// @ts-nocheck
/**
 * SessionCanvas - Block-based Canvas View
 * 参考 AFFiNE 的 Edgeless 模式，提供空间化的知识管理视图
 */
import React, { useState, useCallback, useEffect, forwardRef, useImperativeHandle } from 'react';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  BackgroundVariant,
  Panel,
  ConnectionLineType,
  type Node,
  type Edge,
  type Connection,
  type NodeChange,
  type OnSelectionChangeParams,
  type NodeMouseHandler,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { apiGet, apiPost, apiDelete, apiPatch } from '../../utils/apiClient';
import { logger } from '../../utils/logger';

import PDFSnippetNode from './nodes/PDFSnippetNode';
import QANode from './nodes/QANode';
import StickyNoteNode from './nodes/StickyNoteNode';
import DeepReviewNode from './nodes/DeepReviewNode';
import PDFViewerNode from './nodes/PDFViewerNode';
import PDFCanvasViewer from './PDFCanvasViewer';

import type { Block } from '../../types/block';
import type { CanvasNodeData, SessionCanvasHandle } from '../../types/canvas';
import { NODE_DEFAULT_SIZES } from '../../types/canvas';

import './SessionCanvas.css';

// 注册自定义节点类型
const nodeTypes = {
  pdf_snippet: PDFSnippetNode,
  pdf_viewer: PDFViewerNode,
  qa_normal: QANode,
  qa_quick_scan: QANode,
  qa_deep_dive: QANode,
  qa_critical: QANode,
  qa_socratic: QANode,
  qa_deep_review: DeepReviewNode,
  sticky_note: StickyNoteNode,
};

interface SessionCanvasProps {
  sessionId: string;
  readOnly?: boolean;
  pdfFingerprint?: string | null;
}

/** Extract a display label from block content (best-effort) */
function extractLabel(content: Record<string, unknown>): string {
  return (
    (content?.text as string) ||
    (content?.question as string) ||
    (content?.title as string) ||
    'Block'
  );
}

const SessionCanvas = forwardRef<SessionCanvasHandle, SessionCanvasProps>(
  ({ sessionId, readOnly = false, pdfFingerprint: propPdfFingerprint = null }, ref) => {
  const [nodes, setNodes, onNodesChangeDefault] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(true);
  const [generatingDeepReview, setGeneratingDeepReview] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedNodes, setSelectedNodes] = useState<string[]>([]);

  // 暴露方法给父组件
  useImperativeHandle(ref, () => ({
    addBlock: (block: Block) => {
      const nodeType = block.type;
      const content = (block.content || {}) as Record<string, unknown>;

      const nodeData: CanvasNodeData = {
        type: block.type,
        content: block.content,
        blockId: block.id,
        page: block.page ?? undefined,
        label: extractLabel(content),
      } as CanvasNodeData;

      const newNode: Node<CanvasNodeData> = {
        id: block.id,
        type: nodeType,
        position: {
          x: block.position?.x || block.position_x || 0,
          y: block.position?.y || block.position_y || 0,
        },
        data: nodeData,
        style: {
          width: block.size?.width || block.width || 300,
          height: block.size?.height || block.height || 200,
        },
      };

      setNodes((nds) => [...nds, newNode]);
    },
    refresh: fetchCanvasData
  }));

  // Custom onNodesChange to handle dimension changes
  const onNodesChange = useCallback((changes: NodeChange[]) => {
    onNodesChangeDefault(changes);

    if (!readOnly) {
      changes.forEach(async (change) => {
        if (change.type === 'dimensions' && 'dimensions' in change && change.dimensions) {
          try {
            logger.log('📏 节点大小改变:', change.id, change.dimensions);
            await apiPatch(`/api/blocks/${change.id}`, {
              size: {
                width: change.dimensions.width,
                height: change.dimensions.height
              }
            });
            logger.log('✅ 节点大小已保存');
          } catch (err) {
            console.error('❌ 保存节点大小失败:', err);
          }
        }
      });
    }
  }, [onNodesChangeDefault, readOnly]);

  // PDF background state
  const [pdfFingerprint, setPdfFingerprint] = useState<string | null>(propPdfFingerprint);
  const [currentPdfPage, setCurrentPdfPage] = useState(1);
  const [totalPdfPages, setTotalPdfPages] = useState<number | null>(null);

  useEffect(() => {
    if (propPdfFingerprint && !pdfFingerprint) {
      logger.log('📄 从 prop 设置 pdfFingerprint:', propPdfFingerprint);
      setPdfFingerprint(propPdfFingerprint);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propPdfFingerprint]);

  // Fetch canvas data from backend
  const fetchCanvasData = useCallback(async () => {
    logger.log('🎨 Canvas fetchCanvasData 开始', { sessionId });
    if (!sessionId) {
      logger.log('⚠️ 没有 sessionId，退出');
      return;
    }

    try {
      setLoading(true);
      logger.log('📡 请求 Canvas 数据:', `/api/blocks/canvas/${sessionId}`);
      const response = await apiGet(`/api/blocks/canvas/${sessionId}`) as Record<string, any>;
      logger.log('📦 Canvas API 响应:', response);

      if (!response.success) {
        throw new Error('Failed to fetch canvas data');
      }

      const data = response;
      logger.log('✅ Canvas 数据获取成功:', {
        blocksCount: data.blocks?.length || 0,
        edgesCount: data.edges?.length || 0
      });

      if (data.blocks.length > 0) {
        const firstBlock = data.blocks[0];
        logger.log('📄 第一个 block:', firstBlock);
        if (firstBlock.pdf_fingerprint) {
          logger.log('✅ 设置 PDF fingerprint:', firstBlock.pdf_fingerprint);
          setPdfFingerprint(firstBlock.pdf_fingerprint);
        } else {
          logger.log('⚠️ 第一个 block 没有 pdf_fingerprint');
        }
      } else {
        logger.log('⚠️ 没有 blocks 数据');
      }

      const nodeBlocks = data.blocks.filter((block: any) => block.type !== 'link');
      const linkBlocks = data.blocks.filter((block: any) => block.type === 'link');

      const flowNodes: Node[] = nodeBlocks.map((block: any) => {
        const nodeType = block.type;
        const content = block.content || {};

        const nodeData: CanvasNodeData = {
          type: block.type,
          content,
          blockId: block.id,
          page: block.page,
          label: extractLabel(content),
        } as CanvasNodeData;

        const defaultSize = NODE_DEFAULT_SIZES[nodeType as keyof typeof NODE_DEFAULT_SIZES]
          || { width: 250, height: 160 };

        return {
          id: block.id,
          type: nodeType,
          position: {
            x: block.position_x || block.position?.x || 0,
            y: block.position_y || block.position?.y || 0,
          },
          data: nodeData,
          style: {
            width: block.width || defaultSize.width,
            height: block.height || defaultSize.height,
          },
        };
      });

      const linkEdges: Edge[] = linkBlocks
        .map((block: any) => ({
          id: block.id,
          source: block.content?.source_id,
          target: block.content?.target_id,
          type: 'smoothstep',
          animated: true,
          style: {
            stroke: '#818CF8',
            strokeWidth: 2,
          },
        }))
        .filter((edge: Edge) => edge.source && edge.target);

      const flowEdges = [...(data.edges || []), ...linkEdges];

      logger.log('🎯 设置 nodes:', flowNodes.length, 'edges:', flowEdges.length);
      setNodes(flowNodes);
      setEdges(flowEdges);
      setError(null);
    } catch (err: any) {
      console.error('❌ Canvas 获取数据失败:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      logger.log('✅ Canvas 加载完成');
    }
  }, [sessionId, setNodes, setEdges]);

  useEffect(() => {
    fetchCanvasData();
  }, [fetchCanvasData]);

  const onNodeDragStop: NodeMouseHandler = useCallback(
    async (_event, node) => {
      if (readOnly) return;

      try {
        await apiPatch(`/api/blocks/${node.id}/position`, {
          x: node.position.x,
          y: node.position.y,
        });
        logger.log('✅ 节点位置已更新:', node.id);
      } catch (err) {
        console.error('❌ 更新节点位置失败:', err);
      }
    },
    [readOnly]
  );

  const onConnect = useCallback(
    async (params: Connection) => {
      if (readOnly) return;

      const newEdge: Edge = {
        id: `edge-${params.source}-${params.target}-${Date.now()}`,
        source: params.source!,
        target: params.target!,
        sourceHandle: params.sourceHandle ?? undefined,
        targetHandle: params.targetHandle ?? undefined,
        type: 'smoothstep',
        animated: true,
        style: {
          stroke: '#818CF8',
          strokeWidth: 2,
        },
      };
      setEdges((eds) => addEdge(newEdge, eds));
      logger.log('✅ 连线已创建:', newEdge.id);

      try {
        await apiPost('/api/blocks/', {
          type: 'link',
          session_id: sessionId,
          content: {
            source_id: params.source,
            target_id: params.target,
            label: '',
            type: 'related',
          },
        });
        logger.log('✅ 连线已保存到后端');
      } catch (err) {
        console.warn('⚠️ 连线保存到后端失败，但前端已显示:', err);
      }
    },
    [sessionId, readOnly, setEdges]
  );

  // 智能布局算法：按类型分组排列
  const autoLayout = useCallback(async () => {
    if (nodes.length === 0) return;

    const typeGroups: Record<string, Node[]> = {
      qa: [],
      sticky: [],
      snippet: [],
      review: [],
      other: []
    };

    nodes.forEach(node => {
      if (node.type?.startsWith('qa_')) {
        if (node.type === 'qa_deep_review') {
          typeGroups.review.push(node);
        } else {
          typeGroups.qa.push(node);
        }
      } else if (node.type === 'sticky_note') {
        typeGroups.sticky.push(node);
      } else if (node.type === 'pdf_snippet') {
        typeGroups.snippet.push(node);
      } else {
        typeGroups.other.push(node);
      }
    });

    const PADDING = 30;
    const GROUP_GAP = 60;
    let currentX = PADDING;
    const layoutNodes: Node[] = [];

    Object.entries(typeGroups).forEach(([_groupName, groupNodes]) => {
      if (groupNodes.length === 0) return;

      let currentY = PADDING;
      let maxWidth = 0;

      groupNodes.forEach(node => {
        const width = (node.style?.width as number) || 250;
        const height = (node.style?.height as number) || 160;

        layoutNodes.push({
          ...node,
          position: { x: currentX, y: currentY }
        });

        currentY += height + PADDING;
        maxWidth = Math.max(maxWidth, width);
      });

      currentX += maxWidth + GROUP_GAP;
    });

    setNodes(layoutNodes);

    try {
      await Promise.all(
        layoutNodes.map(node =>
          apiPatch(`/api/blocks/${node.id}/position`, {
            x: node.position.x,
            y: node.position.y,
          })
        )
      );
      logger.log('✅ 自动布局完成，已保存所有位置');
    } catch (err) {
      console.error('❌ 保存布局失败:', err);
    }
  }, [nodes, setNodes]);

  // 碰撞检测：找到不与现有节点重叠的空闲位置
  const findFreePosition = useCallback((newWidth = 200, newHeight = 150) => {
    const PADDING = 30;

    if (nodes.length === 0) {
      return { x: 100, y: 100 };
    }

    const nodeBounds = nodes.map(node => ({
      left: node.position.x,
      right: node.position.x + ((node.style?.width as number) || 250),
      top: node.position.y,
      bottom: node.position.y + ((node.style?.height as number) || 160)
    }));

    const maxRight = Math.max(...nodeBounds.map(b => b.right));
    const maxBottom = Math.max(...nodeBounds.map(b => b.bottom));

    let candidateX = maxRight + PADDING;
    let candidateY = 100;

    const isOverlapping = (x: number, y: number, w: number, h: number) => {
      return nodeBounds.some(bound =>
        !(x + w < bound.left || x > bound.right || y + h < bound.top || y > bound.bottom)
      );
    };

    if (candidateX > 1500) {
      candidateX = 100;
      candidateY = maxBottom + PADDING;
    }

    if (isOverlapping(candidateX, candidateY, newWidth, newHeight)) {
      const gridStep = 50;
      for (let y = 100; y < maxBottom + 500; y += gridStep) {
        for (let x = 100; x < maxRight + 500; x += gridStep) {
          if (!isOverlapping(x, y, newWidth, newHeight)) {
            return { x, y };
          }
        }
      }
    }

    return { x: candidateX, y: candidateY };
  }, [nodes]);

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen((prev) => !prev);
  }, []);

  const openInNewWindow = useCallback(() => {
    const canvasUrl = `${window.location.origin}/?canvas=true&session=${sessionId}`;
    const newWindow = window.open(
      canvasUrl,
      'canvas-window',
      'width=1400,height=900'
    );

    if (newWindow) {
      newWindow.focus();
    } else {
      alert('弹窗被阻止，请允许弹窗后重试');
    }
  }, [sessionId]);

  const addStickyNote = useCallback(async () => {
    if (readOnly) return;

    try {
      const { x: posX, y: posY } = findFreePosition(180, 120);

      const response = await apiPost('/api/blocks/', {
        type: 'sticky_note',
        session_id: sessionId,
        pdf_fingerprint: pdfFingerprint,
        content: {
          text: '新便签',
          color: 'yellow'
        },
        position: { x: posX, y: posY },
        size: { width: 180, height: 120 }
      }) as any;

      if (response.success && response.block) {
        const newNode: Node = {
          id: response.block.id,
          type: 'sticky_note',
          position: {
            x: response.block.position?.x || response.block.position_x || posX,
            y: response.block.position?.y || response.block.position_y || posY
          },
          data: {
            label: response.block.content.text || '新便签',
            color: response.block.content.color || '#FFE66D',
            blockId: response.block.id
          }
        };
        setNodes((nds) => [...nds, newNode]);
      }
    } catch (err: any) {
      console.error('Failed to add sticky note:', err);
      alert('添加便签失败: ' + err.message);
    }
  }, [readOnly, sessionId, pdfFingerprint, setNodes, findFreePosition]);

  const addTextBlock = useCallback(async () => {
    if (readOnly) return;

    try {
      const { x: posX, y: posY } = findFreePosition(240, 160);

      const response = await apiPost('/api/blocks/', {
        type: 'pdf_snippet',
        session_id: sessionId,
        pdf_fingerprint: pdfFingerprint,
        content: {
          text: '双击编辑文本...',
          title: '文本块'
        },
        position: { x: posX, y: posY },
        size: { width: 240, height: 160 }
      }) as any;

      if (response.success && response.block) {
        const newNode: Node = {
          id: response.block.id,
          type: 'pdf_snippet',
          position: {
            x: response.block.position?.x || response.block.position_x || posX,
            y: response.block.position?.y || response.block.position_y || posY
          },
          data: {
            label: response.block.content.title || '文本块',
            text: response.block.content.text || '双击编辑文本...',
            page: null,
            blockId: response.block.id
          }
        };
        setNodes((nds) => [...nds, newNode]);
      }
    } catch (err: any) {
      console.error('Failed to add text block:', err);
      alert('添加文本块失败: ' + err.message);
    }
  }, [readOnly, sessionId, pdfFingerprint, setNodes, findFreePosition]);

  const generateDeepReview = useCallback(async () => {
    if (readOnly) return;
    if (!pdfFingerprint) {
      alert('无法获取PDF指纹，请稍后重试');
      return;
    }

    if (!window.confirm('生成深度审稿可能需要1-2分钟，是否继续？')) {
      return;
    }

    setGeneratingDeepReview(true);
    try {
      logger.log('🚀 开始生成深度审稿...');
      const response = await apiPost('/api/blocks/generate/deep-review', {
        session_id: sessionId,
        pdf_fingerprint: pdfFingerprint,
        force_zhipu_parsing: true
      }) as any;

      if (response.success && response.block) {
        logger.log('✅ 深度审稿生成成功:', response.block);

        const block = response.block;
        const newNode: Node = {
          id: block.id,
          type: 'qa_deep_review',
          position: {
            x: block.position?.x || 400,
            y: block.position?.y || 100
          },
          data: {
            content: block.content,
            blockId: block.id,
            label: '深度审稿'
          },
          style: {
            width: block.size?.width || 420,
            height: block.size?.height || 350
          }
        };

        setNodes((nds) => [...nds, newNode]);
        alert('深度审稿报告已生成！');
      }
    } catch (err: any) {
      console.error('❌ 生成深度审稿失败:', err);
      alert('生成失败: ' + (err.message || '未知错误'));
    } finally {
      setGeneratingDeepReview(false);
    }
  }, [readOnly, sessionId, pdfFingerprint, setNodes]);

  const deleteSelectedNodes = useCallback(async () => {
    if (readOnly || selectedNodes.length === 0) return;

    if (!window.confirm(`确定要删除选中的 ${selectedNodes.length} 个节点吗？`)) {
      return;
    }

    try {
      logger.log('🗑️ 开始删除节点:', selectedNodes);

      await Promise.all(
        selectedNodes.map(nodeId =>
          apiDelete(`/api/blocks/${nodeId}`)
        )
      );

      logger.log('✅ 后端删除成功');

      setNodes((nds) => nds.filter(node => !selectedNodes.includes(node.id)));
      setEdges((eds) => eds.filter(edge =>
        !selectedNodes.includes(edge.source) && !selectedNodes.includes(edge.target)
      ));
      setSelectedNodes([]);

      logger.log('✅ UI更新完成');
    } catch (err: any) {
      console.error('❌ 删除节点失败:', err);
      alert('删除节点失败: ' + err.message);
    }
  }, [readOnly, selectedNodes, setNodes, setEdges]);

  const onSelectionChange = useCallback(({ nodes: selectedNodesList }: OnSelectionChangeParams) => {
    setSelectedNodes(selectedNodesList.map(n => n.id));
  }, []);

  const handlePdfPageChange = useCallback((page: number, totalPages?: number) => {
    setCurrentPdfPage(page);
    if (totalPages !== undefined) {
      setTotalPdfPages(totalPages);
    }
  }, []);

  const nodeColor = (node: Node): string => {
    switch (node.type) {
      case 'pdf_snippet':
        return '#E3F2FD';
      case 'qa_quick_scan':
        return '#FFF3CD';
      case 'qa_deep_dive':
        return '#D1ECF1';
      case 'qa_normal':
        return '#D4EDDA';
      case 'sticky_note':
        return '#FFF9C4';
      default:
        return '#E0E0E0';
    }
  };

  if (loading) {
    return (
      <div className="canvas-loading">
        <div className="spinner"></div>
        <p>加载 Canvas 中...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="canvas-error">
        <p>❌ 加载失败: {error}</p>
        <button onClick={fetchCanvasData}>重试</button>
      </div>
    );
  }

  return (
    <div className={`session-canvas-container ${isFullscreen ? 'fullscreen' : ''}`}>
      {generatingDeepReview && (
        <div className="canvas-generating-toast">
          <div className="generating-card">
            <div className="generating-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
              </svg>
            </div>
            <div className="generating-text">
              <p>正在生成深度审稿</p>
              <span className="generating-hint">AI 分析中，约 1-2 分钟</span>
            </div>
          </div>
        </div>
      )}

      {pdfFingerprint && (
        <PDFCanvasViewer
          pdfFingerprint={pdfFingerprint}
          currentPage={currentPdfPage}
          onPageChange={handlePdfPageChange}
          totalPages={totalPdfPages ?? undefined}
        />
      )}

      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeDragStop={onNodeDragStop}
        onSelectionChange={onSelectionChange}
        nodeTypes={nodeTypes}
        panOnDrag={[1, 2]}
        selectionOnDrag={false}
        panOnScroll={true}
        zoomOnScroll={true}
        zoomOnDoubleClick={false}
        fitView
        fitViewOptions={{
          padding: 0.3,
          maxZoom: 0.85,
        }}
        minZoom={0.1}
        maxZoom={2}
        defaultViewport={{ x: 0, y: 0, zoom: 0.75 }}
        attributionPosition="bottom-left"
        connectionLineType={ConnectionLineType.SmoothStep}
        connectionLineStyle={{
          stroke: '#6366F1',
          strokeWidth: 2.5,
          strokeDasharray: '6 3',
        }}
        defaultEdgeOptions={{
          type: 'smoothstep',
          animated: true,
          style: {
            stroke: '#818CF8',
            strokeWidth: 2,
          },
        }}
        snapToGrid={true}
        snapGrid={[20, 20]}
      >
        <Background variant={BackgroundVariant.Dots} gap={20} size={1} />

        <MiniMap
          nodeColor={nodeColor}
          nodeStrokeWidth={3}
          zoomable
          pannable
        />

        <Controls />

        {!readOnly && (
          <Panel position="top-left" className="canvas-toolbar">
            <div className="toolbar-section">
              <span className="toolbar-section-title">添加</span>
              <button
                className="toolbar-btn"
                onClick={addStickyNote}
                title="添加便签"
              >
                📝 便签
              </button>
              <button
                className="toolbar-btn"
                onClick={addTextBlock}
                title="添加文本块"
              >
                📄 文本块
              </button>
              <button
                className="toolbar-btn"
                onClick={generateDeepReview}
                title="AI深度审稿 (Maker-Checker)"
                style={{ backgroundColor: '#e3f2fd', color: '#1565c0' }}
              >
                🧐 深度审稿
              </button>
            </div>
            <div className="toolbar-divider"></div>
            <div className="toolbar-section">
              <span className="toolbar-section-title">编辑</span>
              <button
                className="toolbar-btn"
                onClick={deleteSelectedNodes}
                disabled={selectedNodes.length === 0}
                title={selectedNodes.length > 0 ? `删除选中的 ${selectedNodes.length} 个节点` : "删除节点（先选中节点）"}
              >
                🗑️ 删除 {selectedNodes.length > 0 && `(${selectedNodes.length})`}
              </button>
              <button
                className="toolbar-btn"
                onClick={autoLayout}
                title="自动布局"
              >
                🔄 布局
              </button>
            </div>
            <div className="toolbar-divider"></div>
            <div className="toolbar-section">
              <span className="toolbar-section-title">视图</span>
              <button
                className="toolbar-btn"
                onClick={toggleFullscreen}
                title={isFullscreen ? "退出全屏" : "全屏模式"}
              >
                {isFullscreen ? '⇱ 退出' : '⛶ 全屏'}
              </button>
              <button
                className="toolbar-btn"
                onClick={openInNewWindow}
                title="在新窗口中打开"
              >
                🗗 新窗口
              </button>
              <button
                className="toolbar-btn"
                onClick={fetchCanvasData}
                title="刷新"
              >
                ↻ 刷新
              </button>
            </div>
            <div className="toolbar-divider"></div>
            <span className="toolbar-info">
              {nodes.length} 个块 · {edges.length} 个连接
            </span>
          </Panel>
        )}

        <Panel position="top-right" className="canvas-info">
          <div className="info-item">
            <span className="info-label">会话 ID:</span>
            <span className="info-value">{sessionId?.substring(0, 8)}...</span>
          </div>
          {readOnly && (
            <div className="info-badge readonly-badge">🔒 只读模式</div>
          )}
        </Panel>
      </ReactFlow>
    </div>
  );
});

export default SessionCanvas;
